'use client';

/**
 * SellerResponseQuadrantCard — mode5's prescriptive canvas surface.
 *
 * 4-cell quadrant: urgency × catalogue-fit. Each RFQ is server-side
 * bucketed (BFF response carries a `quadrant` field per RFQ), so the
 * frontend doesn't recompute thresholds — single source of truth.
 *
 * Layer 1 chrome (titles, hints, urgency interpretive labels,
 * relationship chips) is server-driven via `seller_labels` — every
 * displayed string routes through `useSellerLabel(key, vars?)`.
 * Frontend owns: the 2×2 grid layout, click semantics, color scheme.
 *
 * Click an RFQ card → selectNode + setExplorerSelection('rfq') flips
 * the canvas to mode5_quote_form (same path as the explorer card).
 *
 * @see klyde_edge/ui_config/composition/cards/seller.py — content_key="seller_response_quadrant"
 * @see klyde_edge/ui_config/labels/seller.py — every label key
 * @see klyde_edge/vendor_portal/queries.py — get_vendor_rfqs_priority cypher
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useEffect, useState, useMemo } from 'react';
import {
  Zap, Target, Rabbit, Archive,
  CheckCircle2, XCircle, AlarmClock,
  Loader2, Inbox,
} from 'lucide-react';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useDevUserStore } from '@/lib/stores/dev-user-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useSellerLabel } from '@/lib/stores/total-matrix-store';
import { api } from '@/lib/api/client';
import { text, surfaceBase } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import type { VPRfqPriority, VPQuadrant, VPPriorityListResponse } from '@/lib/api/types';

interface QuadrantSpec {
  key: VPQuadrant;
  titleKey: string;
  hintKey: string;
  icon: typeof Zap;
  cellClass: string;
  iconClass: string;
}

const QUADRANTS: QuadrantSpec[] = [
  {
    key: 'critical',
    titleKey: 'quadrant_critical_title',
    hintKey: 'quadrant_critical_hint',
    icon: Zap,
    cellClass: 'bg-destructive/5 border-destructive/30',
    iconClass: 'text-destructive',
  },
  {
    key: 'strategic',
    titleKey: 'quadrant_strategic_title',
    hintKey: 'quadrant_strategic_hint',
    icon: Target,
    cellClass: 'bg-primary/5 border-primary/30',
    iconClass: 'text-primary',
  },
  {
    key: 'quick_wins',
    titleKey: 'quadrant_quick_wins_title',
    hintKey: 'quadrant_quick_wins_hint',
    icon: Rabbit,
    cellClass: 'bg-amber-500/5 border-amber-500/30',
    iconClass: 'text-amber-600 dark:text-amber-400',
  },
  {
    key: 'backlog',
    titleKey: 'quadrant_backlog_title',
    hintKey: 'quadrant_backlog_hint',
    icon: Archive,
    cellClass: 'bg-muted/30 border-border',
    iconClass: 'text-muted-foreground',
  },
];

export function SellerResponseQuadrantCard() {
  const selectedUser = useDevUserStore((s) => s.selectedUser);
  const priorityStrategy = useCapabilityStore((s) => s.strategyName('vendor_portal', 'prioritize'));
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const setExplorerSelection = useLayoutStore((s) => s.setExplorerSelection);

  // Top-level labels (templated ones called inline at JSX site)
  const labelHeader = useSellerLabel('quadrant_header');
  const labelLoading = useSellerLabel('quadrant_loading');
  const labelEmpty = useSellerLabel('quadrant_empty');

  const [data, setData] = useState<VPPriorityListResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedUser || !priorityStrategy) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    api
      .getStrategyData<VPPriorityListResponse>(priorityStrategy, {})
      .then((res) => {
        if (cancelled) return;
        if (!res.success) {
          setError(res.error || 'Failed to load priority list');
          return;
        }
        setData(res.data ?? null);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message ?? 'Network error');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedUser, priorityStrategy]);

  const grouped = useMemo(() => {
    const out: Record<VPQuadrant, VPRfqPriority[]> = {
      critical: [], strategic: [], quick_wins: [], backlog: [],
    };
    if (data?.open_rfqs) {
      for (const r of data.open_rfqs) out[r.quadrant].push(r);
    }
    return out;
  }, [data]);

  const handleRfqClick = (rfq: VPRfqPriority) => {
    selectNode({
      id: rfq.rfq_sid,
      label: rfq.reference ?? rfq.rfq_sid,
      type: 'rfq',
    });
    setExplorerSelection('rfq');
  };

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        {labelLoading}
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ fontSize: surfaceBase.canvas }} className="flex flex-col items-center justify-center h-full text-destructive">
        <XCircle className="h-6 w-6 mb-2" />
        <p className={text.body}>{error}</p>
      </div>
    );
  }

  if (!data || data.open_rfqs.length === 0) {
    return (
      <div style={{ fontSize: surfaceBase.canvas }} className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Inbox className="h-8 w-8 mb-2 opacity-40" />
        <p className={text.body}>{labelEmpty}</p>
        {data?.closed_summary && (
          <ClosedStrip summary={data.closed_summary} className="mt-6" />
        )}
      </div>
    );
  }

  return (
    <div style={{ fontSize: surfaceBase.canvas }} className="flex flex-col h-full p-4 gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className={text.heading}>{labelHeader}</h2>
          <SubtitleLine
            count={data.open_rfqs.length}
            urgentDays={data.thresholds.urgent_days}
            fitPct={Math.round(data.thresholds.fit_threshold * 100)}
            className={cn(text.muted, 'mt-0.5')}
          />
        </div>
      </div>

      {/* 2×2 grid */}
      <div className="flex-1 min-h-0 grid grid-cols-2 grid-rows-2 gap-3">
        {QUADRANTS.map((q) => (
          <QuadrantCell
            key={q.key}
            spec={q}
            rfqs={grouped[q.key]}
            onRfqClick={handleRfqClick}
          />
        ))}
      </div>

      {/* Closed strip */}
      <ClosedStrip summary={data.closed_summary} />
    </div>
  );
}

function SubtitleLine({
  count,
  urgentDays,
  fitPct,
  className,
}: {
  count: number;
  urgentDays: number;
  fitPct: number;
  className?: string;
}) {
  const label = useSellerLabel('quadrant_subtitle', {
    count,
    urgent_days: urgentDays,
    fit_pct: fitPct,
  });
  return <p className={className}>{label}</p>;
}

function QuadrantCell({
  spec,
  rfqs,
  onRfqClick,
}: {
  spec: QuadrantSpec;
  rfqs: VPRfqPriority[];
  onRfqClick: (rfq: VPRfqPriority) => void;
}) {
  const Icon = spec.icon;
  const totalLines = rfqs.reduce((acc, r) => acc + r.line_count, 0);

  const title = useSellerLabel(spec.titleKey);
  const hint = useSellerLabel(spec.hintKey);
  const lineCountLabel = useSellerLabel('quadrant_cell_count_lines', { count: totalLines });
  const emptyLabel = useSellerLabel('quadrant_cell_empty');

  return (
    <div className={cn('flex flex-col rounded-lg border p-3 min-h-0 overflow-hidden', spec.cellClass)}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className={cn('h-4 w-4', spec.iconClass)} />
          <div>
            <div className={cn(text.title, 'leading-tight')}>{title}</div>
            <div className={cn(text.muted, 'mt-0.5')}>{hint}</div>
          </div>
        </div>
        <div className={cn(text.muted, 'text-right shrink-0')}>
          <div className={cn(text.title, 'tabular-nums')}>{rfqs.length}</div>
          <div className={text.muted}>{lineCountLabel}</div>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto -mx-1 px-1 space-y-1.5">
        {rfqs.length === 0 ? (
          <div className={cn(text.muted, 'text-center py-4 italic opacity-60')}>{emptyLabel}</div>
        ) : (
          rfqs.map((rfq) => <RfqCard key={rfq.rfq_sid} rfq={rfq} onClick={() => onRfqClick(rfq)} />)
        )}
      </div>
    </div>
  );
}

function RfqCard({ rfq, onClick }: { rfq: VPRfqPriority; onClick: () => void }) {
  const days = rfq.days_until_deadline;

  // Urgency tone — drives off `is_urgent` (server-bucketed) instead of
  // re-applying day thresholds. Single source of truth on the BFF.
  const urgencyTone = days != null && days < 0
    ? 'text-destructive font-semibold'
    : rfq.is_urgent
      ? 'text-amber-600 dark:text-amber-400 font-semibold'
      : 'text-muted-foreground';

  // Urgency interpretive label — server labels with placeholder substitution.
  const urgencyText = useUrgencyLabel(days);

  // Buyer relationship chip — server labels.
  const hasPriorPO = rfq.buyer_prior_po_count > 0;
  const buyerChipText = useBuyerChipLabel(rfq);

  // Catalogue fit footer — only when there are lines to compute over.
  const fitText = useSellerLabel('fit_in_catalogue', {
    count: rfq.catalogue_fit_count,
    total: rfq.line_count,
    pct: Math.round(rfq.catalogue_fit_pct * 100),
  });

  const noProjectLabel = useSellerLabel('rfq_card_no_project');
  const linesLabel = useSellerLabel(
    rfq.line_count === 1 ? 'rfq_card_lines_one' : 'rfq_card_lines',
    { count: rfq.line_count },
  );

  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded-md border border-border/60 bg-background hover:bg-accent/50 hover:border-primary/40 transition-colors p-2"
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className={cn(text.title, 'truncate')}>
          {rfq.reference ?? rfq.rfq_sid}
        </span>
        <span className={cn('text-[10px] tabular-nums shrink-0', urgencyTone)}>
          {urgencyText}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className={cn(text.muted, 'truncate')}>
          {rfq.buyer_name ?? noProjectLabel} · {linesLabel}
        </span>
        <span className={cn('text-[9px] px-1.5 py-0.5 rounded shrink-0', hasPriorPO
          ? 'bg-green-500/10 text-green-700 dark:text-green-400'
          : 'bg-muted text-muted-foreground')}>
          {buyerChipText}
        </span>
      </div>
      {rfq.line_count > 0 && (
        <div className={cn(text.muted, 'mt-1 opacity-70')}>
          {fitText}
        </div>
      )}
    </button>
  );
}

function useUrgencyLabel(days: number | null): string {
  // Hooks called unconditionally; pick which result to return based on
  // the input. Keeps rules-of-hooks order stable.
  const noDeadline = useSellerLabel('urgency_no_deadline');
  const today = useSellerLabel('urgency_today');
  const tomorrow = useSellerLabel('urgency_tomorrow');
  const daysLeft = useSellerLabel('urgency_days_left', { count: Math.max(days ?? 0, 0) });
  const overdue = useSellerLabel(
    Math.abs(days ?? 0) === 1 ? 'urgency_overdue_one' : 'urgency_overdue',
    { count: Math.abs(days ?? 0) },
  );

  if (days == null) return noDeadline;
  if (days < 0) return overdue;
  if (days === 0) return today;
  if (days === 1) return tomorrow;
  return daysLeft;
}

function useBuyerChipLabel(rfq: VPRfqPriority): string {
  const newBuyer = useSellerLabel('buyer_new');
  const priorCount = useSellerLabel('buyer_prior_count', { count: rfq.buyer_prior_po_count });
  const priorWithValue = useSellerLabel('buyer_prior_count_value', {
    count: rfq.buyer_prior_po_count,
    value: rfq.buyer_prior_po_value
      ? (rfq.buyer_prior_po_value / 1000).toFixed(1)
      : '0',
  });

  if (rfq.buyer_prior_po_count === 0) return newBuyer;
  if (rfq.buyer_prior_po_value && rfq.buyer_prior_po_value > 0) return priorWithValue;
  return priorCount;
}

function ClosedStrip({
  summary,
  className,
}: {
  summary: { won_count: number; won_value: number; lost_count: number; expired_count: number; window_days: number };
  className?: string;
}) {

  const windowLabel = useSellerLabel('closed_window_label', { days: summary.window_days });
  const wonLabel = useSellerLabel(
    summary.won_value > 0 ? 'closed_won_value' : 'closed_won',
    {
      count: summary.won_count,
      value: summary.won_value > 0 ? (summary.won_value / 1000).toFixed(1) : '0',
    },
  );
  const lostLabel = useSellerLabel('closed_lost', { count: summary.lost_count });
  const expiredLabel = useSellerLabel('closed_expired', { count: summary.expired_count });

  return (
    <div className={cn('flex items-center gap-4 px-3 py-2 rounded-lg border border-border/60 bg-muted/30', className)}>
      <span className={cn(text.eyebrow, 'opacity-70 shrink-0')}>
        {windowLabel}
      </span>
      <span className="flex items-center gap-1.5">
        <CheckCircle2 className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
        <span className={text.body}>{wonLabel}</span>
      </span>
      <span className="flex items-center gap-1.5">
        <XCircle className="h-3.5 w-3.5 text-muted-foreground" />
        <span className={text.body}>{lostLabel}</span>
      </span>
      <span className="flex items-center gap-1.5">
        <AlarmClock className="h-3.5 w-3.5 text-muted-foreground" />
        <span className={text.body}>{expiredLabel}</span>
      </span>
    </div>
  );
}
